/**
 * React Query hooks for per-user backend config storage.
 * Use these in SIP hooks; do not call userConfigApi directly in components.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userConfigApi } from '@/api/services';
import type { UserConfigResponse } from '@/api/services/userConfig';

export function useUserConfig(key: string) {
  return useQuery<UserConfigResponse | null>({
    queryKey: ['userConfig', key],
    queryFn: async () => {
      try {
        return await userConfigApi.get(key);
      } catch (err) {
        const status = (err as { status?: number })?.status;
        // 404 means no config saved yet — treat as null
        if (status === 404) return null;
        // Re-throw so React Query retries on network/5xx errors
        throw err;
      }
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 3,
  });
}

export function useSaveUserConfig() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      userConfigApi.save(key, value),
    onSuccess: (data, { key }) => {
      queryClient.setQueryData(['userConfig', key], data);
    },
  });

  return {
    execute: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}
